import React, {
  FC,
  useEffect,
  useState,
} from 'react';
import cx from 'classnames';
import { useFormik } from 'formik';
import {
  ButtonV4,
  Modal,
  NoData,
  Paginate,
  Preloader,
  RuTube,
  VKVideo,
} from 'components';
import { routes } from 'appConstants';
import { useRequest } from 'hooks';
import {
  _has_t_back,
  _t,
  _t_back,
  dateToUiFormat,
} from 'utils';
import type { GetResponse, News } from 'types';
import { RequestStatus } from 'types';

import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';

import newsStyles from '../MediaNews/styles.module.scss';
import styles from './styles.module.scss';

type Action = 'detail' | 'external' | 'opener';

type Props = {
  requestUrl: string,
  badge: string,
  action?: Action,
  pageSize?: number,
};

type Params = {
  page: number,
};

const MediaV4List: FC<Props> = ({
  requestUrl,
  badge,
  action = 'detail',
  pageSize = 16,
}) => {
  const [openModalId, setOpenModalId] = useState<News | null>(null);
  const {
    data,
    request,
    status,
  } = useRequest<GetResponse<News[]>>();

  const {
    values,
    handleChange,
    handleSubmit,
  } = useFormik<Params>({
    initialValues: { page: 0 },
    onSubmit: (params) => {
      request({
        method: 'get',
        url: requestUrl,
        params: {
          limit: pageSize,
          offset: params.page * pageSize,
        },
      });
    },
  });

  useEffect(() => {
    request({
      method: 'get',
      url: requestUrl,
      params: {
        limit: pageSize,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestUrl, pageSize]);

  const listOfData = (data?.results || []).filter(_has_t_back);
  const closeModal = () => setOpenModalId(null);

  const getVideoUrl = (item: News | null) => {
    if (!item) {
      return '';
    }
    return String(
      item.video ||
      item.youtube_url ||
      _t_back(item, 'video') ||
      '',
    );
  };

  return (
    <>
      <section className={newsStyles.list}>
        <Preloader isLoading={status === RequestStatus.REQUEST}>
          <NoData count={listOfData.length || 0}>
            <ul className={newsStyles.grid}>
              {listOfData.map((item, index) => {
                const featured = values.page === 0 && index === 0;
                const withImage = Boolean(item.image) && !featured;
                const tone = (featured || withImage) ? 'onDark' : 'onLight';

                return (
                  <li
                    key={item.id}
                    className={cx(
                      newsStyles.card,
                      featured && newsStyles.cardFeatured,
                      withImage && newsStyles.cardImage,
                    )}
                    style={withImage ? {
                      ['--news-photo' as string]: `url(${item.image})`,
                    } : undefined}
                  >
                    <span className={newsStyles.badge}>{_t(badge)}</span>
                    <div className={newsStyles.cardBody}>
                      {item.pubdate ? (
                        <time className={newsStyles.date} dateTime={item.pubdate}>
                          {dateToUiFormat(item.pubdate)}
                        </time>
                      ) : null}
                      <h2 className={newsStyles.cardTitle}>{_t_back(item, 'title')}</h2>
                      {action === 'detail' ? (
                        <ButtonV4
                          to={`${routes.media.root}/${item.slug || item.id}`}
                          label="Подробнее"
                          variant="outline"
                          tone={tone}
                          className={newsStyles.detailBtn}
                          iconSrc={iconArrow}
                        />
                      ) : null}
                      {action === 'external' ? (
                        <ButtonV4
                          type="button"
                          label="Подробнее"
                          variant="outline"
                          tone={tone}
                          className={newsStyles.detailBtn}
                          iconSrc={iconArrow}
                          onClick={() => {
                            if (item.url) {
                              window.open(item.url, '_blank', 'noopener,noreferrer');
                            }
                          }}
                        />
                      ) : null}
                      {action === 'opener' ? (
                        <ButtonV4
                          type="button"
                          label="Смотреть"
                          variant="outline"
                          tone={tone}
                          className={newsStyles.detailBtn}
                          iconSrc={iconArrow}
                          onClick={() => setOpenModalId(item)}
                        />
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </NoData>
        </Preloader>
      </section>

      <Paginate
        pageSize={pageSize}
        page={values.page}
        count={data?.count || 0}
        onChange={(selected) => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          handleChange({
            target: {
              name: 'page',
              value: selected,
            },
          });
          handleSubmit();
        }}
      />

      {action === 'opener' ? (
        <Modal
          onClose={closeModal}
          isOpen={openModalId !== null}
          title={_t_back(openModalId, 'title')}
          className={styles.modal}
          classNameTitle={styles.title}
          classNameOverlay={styles.modalOverlay}
        >
          {getVideoUrl(openModalId).indexOf('vk.com') >= 0 ? (
            <VKVideo url={getVideoUrl(openModalId)} />
          ) : (
            <RuTube url={getVideoUrl(openModalId)} />
          )}
        </Modal>
      ) : null}
    </>
  );
};

export default MediaV4List;
